import { useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import { useRequiredURLSearchParam } from "src/hooks/useRequiredURLSearchParam";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "src/layouts/AuthLayout";
import { LoginLinkForm, type LoginLinkFormData } from "src/forms/LoginLinkForm";
import { usePasswordResetConfirmMutation } from "src/hooks/usePasswordResetMutation";

export function LoginLinkPage(props: {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const email = useRequiredURLSearchParam("email", "/");
  const token = useRequiredURLSearchParam("token", "/");

  const passwordResetMutation = usePasswordResetConfirmMutation(token);

  // for this simple state, useState is sufficient, when state becomes a little more complex, think about xstate.
  const [isFinished, setIsFinished] = useState(false);

  function handleClose() {
    navigate("/login");
  }

  async function handleSubmit(values: LoginLinkFormData) {
    passwordResetMutation.mutateAsync(values.password).then(() => {
      setIsFinished(true);
    });
  }

  return render();

  function render() {
    return (
      <AuthLayout>
        <StyledBox height="100%" display="flex" flexDirection="column" justifyContent="center">
          {!isFinished && renderForm()}
          {isFinished && renderFinished()}
        </StyledBox>
      </AuthLayout>
    );
  }

  function renderForm() {
    return (
      <>
        <Box pb={3}>
          <Typography variant="h3" component="h1">
            {t("label.welcome")}
          </Typography>
          <Typography variant="subtitle1" component="h1">
            {t("label.chooseANewPasswordLoginLink", { email })}
          </Typography>
        </Box>

        <LoginLinkForm
          onClose={handleClose}
          onSubmit={handleSubmit}
          errors={passwordResetMutation.error}
          isLoading={passwordResetMutation.isLoading}
        />
      </>
    );
  }

  function renderFinished() {
    return (
      <>
        <Box pb={3}>
          <Typography variant="h3" component="h1">
            {t("label.yourPasswordHasBeenChanged")}
          </Typography>
          <Typography variant="subtitle1" component="h1">
            {t("label.youCanLoginWithYourNewCredentials")}
          </Typography>
        </Box>
        <Grid container>
          <Button color="primary" variant="contained" onClick={() => navigate("/login")}>
            {t("label.login")}
          </Button>
        </Grid>
      </>
    );
  }
}

const StyledBox = styled(Box)`
  padding: ${({ theme }) => theme.spacing(8)};
  width: fit-content;
`;
